import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "project-planning" }
const _hoisted_2 = { class: "planning-header" }
const _hoisted_3 = { class: "header-controls" }
const _hoisted_4 = { class: "month-range" }
const _hoisted_5 = { class: "planning-table-wrapper" }
const _hoisted_6 = { class: "planning-table" }
const _hoisted_7 = { class: "day-header__number" }
const _hoisted_8 = { class: "day-header__weekday" }
const _hoisted_9 = ["title"]
const _hoisted_10 = { class: "sprint-bar__name" }
const _hoisted_11 = { class: "table-footer" }
const _hoisted_12 = { class: "legend" }

import enUK from "@/locales/home/en-UK.json";
import activityFeedI18n from "@/locales/activityFeed/en-UK.json";
import projectI18n from "@/locales/project/en-UK.json";

import { useI18n } from "vue-i18n";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useApolloClient, useMutation, useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";
import { Icon } from "@iconify/vue";

import useMutationHandlers from "@/composables/useMutationHandlers";
import {
  GET_PROJECT_PLANNING,
  GET_PROJECT_USERS,
} from "@/graphql/project/queries";
import { GET_USER } from "@/graphql/user/queries";
import { GET_SPRINTS } from "@/graphql/orcha/sprints/queries";
import {
  UPDATE_SPRINT,
  ARCHIVE_SPRINT,
  CREATE_SPRINT,
} from "@/graphql/orcha/sprints/mutations";
import {
  UPDATE_ACTIVITY,
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "@/graphql/orcha/activities/mutations";
import {
  CREATE_TASK,
  UPDATE_TASK,
  DELETE_TASK,
} from "@/graphql/orcha/tasks/mutations";
import {
  CREATE_COMMENT,
  UPDATE_COMMENT,
  DELETE_COMMENT,
  ON_NOTIFY_MENTIONED_USERS,
} from "@/graphql/orcha/comments/mutations";
import { GET_PLANNED_MAN_DAYS_FOR_SPRINTS } from "@/graphql/planning/queries";
import {
  GET_ACTIVITY,
  GET_BASIC_ACTIVITIES,
  GET_ACTIVITY_STATUSES,
  GET_ACTIVITY_DEV_STATUSES,
} from "@/graphql/orcha/activities/queries";
import { getFileBlob, uploadFile } from "@/services/file";
import dayjs from "dayjs";
import {
  PlannedTime,
  PlannedTimeFrequency,
  TimeOff,
} from "@/helpers/project/typesDefinition";
import type { ProjectAccessRight } from "@/helpers/typesDefinition";
import GraphLegendItem from "@/components/GraphLegendItem.vue";
import {
  AoSprintPlanning,
  type Sprint,
  type Activity,
  type ActivityStatus,
  type ActivityDevStatus,
  type User,
  type AddFilesPayload,
  type RemoveFilePayload,
  type AddCommentPayload,
  type UpdateCommentPayload,
  type RemoveCommentPayload,
  type Attachment,
  type UpdateActivityPayload,
  type AddSubtaskPayload,
  type UpdateSubtaskFieldPayload,
} from "@26lights/orcha";

type MessageSchema = typeof enUK & typeof activityFeedI18n & typeof projectI18n;

interface Props {
  id?: string;
  isEditable?: boolean | "only-own";
}

type PlanningUser = PlannedTime["user"];
type PlannedTimeWithoutUser = Omit<PlannedTime, "user">;
type TimeOffWithoutUser = Omit<TimeOff, "user">;

interface PlanningUserInfo {
  user: PlanningUser;
  plannedTimes: PlannedTimeWithoutUser[];
  timeOffs: TimeOffWithoutUser[];
}

type SprintLaneItem = Sprint & {
  startIndex: number;
  endIndex: number;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectPlanning',
  props: {
    id: {},
    isEditable: { type: [Boolean, String], default: true }
  },
  setup(__props: any) {

_useCssVars(_ctx => ({
  "b7bb1b22": (nbWorkingDays.value),
  "v14242464": (sprintLaneCount.value),
  "v483d328f": (nbUsers.value),
  "v7f0ebacf": (projectColor.value)
}))

const getSprintName = (sprint: Sprint): string =>
  sprint.label
    ? `Sprint ${sprint.number}: ${sprint.label}`
    : `Sprint ${sprint.number}`;

const props = __props;

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": {
      ...enUK,
      ...activityFeedI18n,
      ...projectI18n,
    },
  },
});

const { createErrorHandler } = useMutationHandlers();
const router = useRouter();
const toast = useToast();
const { client: apolloClient } = useApolloClient();

const isPlanningExpanded = ref(true);

const currentStartDate = ref(dayjs(new Date()).startOf("month"));

const startDate = computed(() => currentStartDate.value);
const endDate = computed(() => startDate.value.add(2, "months"));

const startDateFormatted = computed(() => startDate.value.format("YYYY-MM-DD"));
const endDateFormatted = computed(() => endDate.value.format("YYYY-MM-DD"));

// Reactive variables for the query - must be defined before useQuery
const queryVariables = computed(() => ({
  projectId: props.id,
  startDate: startDateFormatted.value,
  endDate: endDateFormatted.value,
}));

const { result: workPeriodResult, onError } = useQuery(
  GET_PROJECT_PLANNING,
  queryVariables,
);
onError(createErrorHandler("getworkPeriod"));

// Navigation functions
const previousPeriod = () => {
  currentStartDate.value = currentStartDate.value.subtract(1, "month");
};

const nextPeriod = () => {
  currentStartDate.value = currentStartDate.value.add(1, "month");
};

const goToToday = () => {
  currentStartDate.value = dayjs(new Date()).startOf("month");
};

// Computed label for the current month range
const monthRangeLabel = computed(() => {
  const start = startDate.value;
  const end = startDate.value.add(1, "month");
  return `${start.format("MMMM YYYY")} - ${end.format("MMMM YYYY")}`;
});

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (event.defaultPrevented) return;

  const target = event.target as HTMLElement;

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  ) {
    return;
  }

  if (event.key === "ArrowLeft") {
    previousPeriod();
  } else if (event.key === "ArrowRight") {
    nextPeriod();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
});

const { result: sprintsResult, onError: sprintsError } = useQuery(
  GET_SPRINTS,
  () => ({ projectId: props.id, onlyActive: false }),
  // for better performance, you might consider not requesting non-active
  // sprint if checkbox "show archived" isn't checked in the future
);
sprintsError(createErrorHandler("getsprints"));

const sprints = computed<Sprint[]>(() => {
  return sprintsResult.value?.sprints ?? [];
});

// Build sprint configs for ALL sprints (for planning capacity)
const sprintConfigs = computed(() => {
  return sprints.value
    .map((sprint) => ({
      projectId: sprint.projectId,
      startDate: sprint.startDate,
      endDate: sprint.endDate,
    }))
    .filter(
      (
        config,
      ): config is { projectId: string; startDate: string; endDate: string } =>
        Boolean(config.projectId && config.startDate && config.endDate),
    );
});

// Sprint planning data query - loads planned man-days for all sprints
const { result: sprintPlanningResult } = useQuery(
  GET_PLANNED_MAN_DAYS_FOR_SPRINTS,
  () => ({
    sprints: sprintConfigs.value,
    userIds: undefined,
  }),
  () => ({
    enabled: sprintConfigs.value.length > 0,
  }),
);

// Transform sprint planning result into a map by sprint ID
const sprintPlanningData = computed(() => {
  if (!sprintPlanningResult.value?.plannedManDaysForSprints) return {};

  const data: Record<
    string,
    {
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }
  > = {};

  sprintPlanningResult.value.plannedManDaysForSprints.forEach(
    (planning: {
      projectId: string;
      startDate: string;
      endDate: string;
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }) => {
      // Find the matching sprint by projectId + startDate + endDate
      const matchingSprint = sprints.value.find(
        (s: { projectId: string; startDate: string; endDate: string }) =>
          s.projectId === planning.projectId &&
          s.startDate === planning.startDate &&
          s.endDate === planning.endDate,
      );
      if (matchingSprint) {
        data[matchingSprint.id] = {
          plannedManDays: planning.plannedManDays,
          remainingPlannedManDays: planning.remainingPlannedManDays,
          users: planning.users ?? [],
        };
      }
    },
  );

  return data;
});

// Activity detail support data
const { result: activityStatusesResult, onError: onActivityStatusesError } =
  useQuery(GET_ACTIVITY_STATUSES);
onActivityStatusesError(createErrorHandler("getActivityStatuses"));
const activityStatuses = computed<ActivityStatus[]>(
  () => activityStatusesResult.value?.activityStatuses ?? [],
);

const {
  result: activityDevStatusesResult,
  onError: onActivityDevStatusesError,
} = useQuery(GET_ACTIVITY_DEV_STATUSES);
onActivityDevStatusesError(createErrorHandler("getActivityDevStatuses"));
const activityDevStatuses = computed<ActivityDevStatus[]>(
  () => activityDevStatusesResult.value?.activityDevStatuses ?? [],
);

const { result: userResult, onError: onUserError } = useQuery(GET_USER);
onUserError(createErrorHandler("getUser"));
const currentUserId = computed(() => userResult.value?.user?.id);

const { result: projectUsersResult, onError: onProjectUsersError } = useQuery(
  GET_PROJECT_USERS,
  () => ({ projectId: props.id }),
  () => ({ enabled: Boolean(props.id) }),
);
onProjectUsersError(createErrorHandler("getProjectUsers"));
const projectUsers = computed<User[]>(() => {
  return (projectUsersResult.value?.project?.accessRights?.items ?? [])
    .map((accessRight: ProjectAccessRight) => accessRight.user)
    .filter((user: User) => user.firstName && user.lastName);
});

// AoSprintPlanning owns selection/navigation. Fides only mirrors the selected
// activity id so it can lazily load the full Activity used by AoActivityDetails.
const selectedActivityId = ref<string | null>(null);
const selectedActivity = ref<Activity>();
let activityRequestSequence = 0;

const loadSelectedActivity = async (activityId: string | null) => {
  if (!activityId) {
    selectedActivity.value = undefined;
    return;
  }

  const requestSequence = ++activityRequestSequence;
  const { data } = await apolloClient.query({
    query: GET_ACTIVITY,
    variables: { id: activityId },
    fetchPolicy: "network-only",
  });

  if (
    requestSequence === activityRequestSequence &&
    selectedActivityId.value === activityId
  ) {
    selectedActivity.value = data?.activity;
  }
};

watch(
  selectedActivityId,
  async (activityId) => {
    if (!activityId) {
      activityRequestSequence += 1;
      selectedActivity.value = undefined;
      return;
    }

    try {
      await loadSelectedActivity(activityId);
    } catch (error) {
      console.error("Failed to load activity detail", error);
    }
  },
  { immediate: true },
);

const selectActivity = (activityId: string | null) => {
  selectedActivityId.value = activityId;
};

const loadAttachmentPreview = async (attachment: Attachment): Promise<Blob> => {
  if (!attachment.url) {
    throw new Error("Attachment URL is missing");
  }

  return getFileBlob(attachment.url);
};

// Query activities for the project
const {
  result: activitiesResult,
  onError: activitiesError,
  refetch: refetchActivities,
} = useQuery(GET_BASIC_ACTIVITIES, () => ({ projectId: [props.id] }));
activitiesError(createErrorHandler("getactivities"));

const activities = computed<Activity[]>(() => {
  return activitiesResult.value?.activities ?? [];
});

const { mutate: updateSprintMutation } = useMutation(UPDATE_SPRINT);
const { mutate: archiveSprintMutation } = useMutation(ARCHIVE_SPRINT);
const { mutate: createSprintMutation } = useMutation(CREATE_SPRINT);
const { mutate: updateActivityMutation } = useMutation(UPDATE_ACTIVITY);
const { mutate: createTaskMutation } = useMutation(CREATE_TASK);
const { mutate: updateTaskMutation } = useMutation(UPDATE_TASK);
const { mutate: deleteTaskMutation } = useMutation(DELETE_TASK);
const { mutate: createAttachmentMutation } = useMutation(CREATE_ATTACHMENT);
const { mutate: deleteAttachmentMutation } = useMutation(DELETE_ATTACHMENT);
const { mutate: createCommentMutation } = useMutation(CREATE_COMMENT);
const { mutate: updateCommentMutation } = useMutation(UPDATE_COMMENT);
const { mutate: deleteCommentMutation } = useMutation(DELETE_COMMENT);
const { mutate: notifyMentionedUsers } = useMutation(ON_NOTIFY_MENTIONED_USERS);

const refetchSprints = [
  {
    query: GET_SPRINTS,
    variables: { projectId: props.id, onlyActive: false },
    // for better performance, you might consider not requesting non-active
    // sprint if checkbox "show archived" isn't checked in the future
  },
];

const handleUpdateSprint = async (payload: {
  id: string;
  projectId: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  color?: string;
}) => {
  try {
    const result = await updateSprintMutation(
      {
        id: payload.id,
        projectId: payload.projectId,
        label: payload.label,
        startDate: payload.startDate,
        endDate: payload.endDate,
        color: payload.color,
      },
      { refetchQueries: refetchSprints },
    );
    if (result?.data?.updateSprint?.status !== "ok") {
      const errorMessage =
        result?.data?.updateSprint?.error ?? "Failed to update sprint";
      toast.error(errorMessage);
    }
  } catch (e) {
    console.error("Failed to update sprint", e);
  }
};

const handleArchiveSprint = async (payload: {
  id: string;
  projectId: string;
}) => {
  try {
    await archiveSprintMutation(
      { id: payload.id, projectId: payload.projectId },
      { refetchQueries: refetchSprints },
    );
  } catch (e) {
    console.error("Failed to archive sprint", e);
  }
};

const handleUnarchiveSprint = async (payload: {
  id: string;
  projectId: string;
}) => {
  try {
    await updateSprintMutation(
      { id: payload.id, projectId: payload.projectId, active: true },
      { refetchQueries: refetchSprints },
    );
  } catch (e) {
    console.error("Failed to unarchive sprint", e);
  }
};

const handleCreateSprint = async (payload: {
  projectId?: string;
  label?: string;
  startDate: string;
  endDate: string;
  color?: string;
}) => {
  const projectId = payload.projectId ?? props.id;
  if (!projectId) return;

  try {
    const result = await createSprintMutation(
      {
        projectId,
        label: payload.label,
        startDate: payload.startDate,
        endDate: payload.endDate,
        color: payload.color,
      },
      { refetchQueries: refetchSprints },
    );
    if (result?.data?.createSprint?.status !== "ok") {
      const errorMessage =
        result?.data?.createSprint?.error ?? "Failed to create sprint";
      toast.error(errorMessage);
    }
  } catch (e) {
    console.error("Failed to create sprint", e);
  }
};

const handleActivityChange = async (payload: {
  id: string;
  sprintId?: string | null;
  milestoneId?: string | null;
}) => {
  const variables: {
    id: string;
    sprintId?: string | null;
    milestoneId?: string | null;
  } = { id: payload.id };

  if (payload.sprintId !== undefined) {
    variables.sprintId = payload.sprintId;
  }

  if (payload.milestoneId !== undefined) {
    variables.milestoneId = payload.milestoneId;
  }

  try {
    await updateActivityMutation(variables, {
      refetchQueries: [
        { query: GET_BASIC_ACTIVITIES, variables: { projectId: [props.id] } },
      ],
    });
  } catch (e) {
    console.error("Failed to update activity", e);
  }
};

// Activity detail mutation handlers
const refreshSelectedActivity = async () => {
  if (selectedActivityId.value) {
    await loadSelectedActivity(selectedActivityId.value);
  }
};

const handleUpdateActivity = async (payload: UpdateActivityPayload) => {
  try {
    await updateActivityMutation(payload);
    await Promise.all([refetchActivities(), refreshSelectedActivity()]);
  } catch (error) {
    console.error("Failed to update activity", error);
  }
};

const handleAddSubtask = async (payload: AddSubtaskPayload) => {
  try {
    await createTaskMutation(payload);
    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to add subtask", error);
  }
};

const handleUpdateSubtask = async (payload: UpdateSubtaskFieldPayload) => {
  try {
    await updateTaskMutation(payload);
    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to update subtask", error);
  }
};

const handleDeleteSubtask = async (payload: { id: string }) => {
  try {
    await deleteTaskMutation({ id: payload.id });
    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to delete subtask", error);
  }
};

const handleAddFiles = async ({ activityId, files }: AddFilesPayload) => {
  try {
    const fileIds = await uploadFile(files);
    await Promise.all(
      fileIds.map((fileId) => createAttachmentMutation({ activityId, fileId })),
    );
    await refreshSelectedActivity();
  } catch (error) {
    logErrorMessages(error);
    toast.error(t("errors.uploadingFile"));
  }
};

const handleRemoveFile = async (payload: RemoveFilePayload) => {
  try {
    await deleteAttachmentMutation({ id: payload.file.id });
    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to remove file", error);
  }
};

const handleAddComment = async (payload: AddCommentPayload) => {
  const { activityId, authorId, message, mentionedUserIds } = payload;

  try {
    const result = await createCommentMutation({
      activityId,
      authorId,
      message,
    });

    if (
      result?.data?.createComment?.status === "ok" &&
      mentionedUserIds.length > 0
    ) {
      const activityRoute = router.resolve({
        name: "ActivityFeed",
        params: { id: activityId },
      });

      await notifyMentionedUsers({
        userIds: mentionedUserIds,
        comment: result.data.createComment.data.message,
        activityName: selectedActivity.value?.name ?? "",
        activityLink: window.location.origin + activityRoute.href,
        authorId,
      });
    }

    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to add comment", error);
  }
};

const handleUpdateComment = async (payload: UpdateCommentPayload) => {
  const { id, message, mentionedUserIds } = payload;

  try {
    const result = await updateCommentMutation({ id, message });

    if (
      result?.data?.updateComment?.status === "ok" &&
      mentionedUserIds.length > 0
    ) {
      const activityRoute = router.resolve({
        name: "ActivityFeed",
        params: { id: selectedActivityId.value },
      });

      await notifyMentionedUsers({
        userIds: mentionedUserIds,
        comment: result.data.updateComment.data.message,
        activityName: selectedActivity.value?.name ?? "",
        activityLink: window.location.origin + activityRoute.href,
        authorId: result.data.updateComment.data.author.id,
      });
    }

    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to update comment", error);
  }
};

const handleRemoveComment = async (payload: RemoveCommentPayload) => {
  try {
    await deleteCommentMutation({ id: payload.id });
    await refreshSelectedActivity();
  } catch (error) {
    console.error("Failed to remove comment", error);
  }
};

const workPeriod = computed(
  () => workPeriodResult.value?.project?.workingPeriod,
);

const planningUsers = computed<Record<string, PlanningUserInfo>>(() => {
  if (!workPeriod.value) return {};

  const usersWithPlannedTime = workPeriod.value.plannedTimes.reduce(
    (acc: Record<string, PlanningUserInfo>, plannedTime: PlannedTime) => {
      const { user, ...plannedTimeData } = plannedTime;
      const currentUserData = acc[user.id];

      if (!currentUserData) {
        return {
          ...acc,
          [user.id]: {
            user,
            plannedTimes: [plannedTimeData],
            timeOffs: [],
          },
        };
      }

      return {
        ...acc,
        [user.id]: {
          ...currentUserData,
          plannedTimes: [...currentUserData.plannedTimes, plannedTimeData],
        },
      };
    },
    {} as Record<string, PlanningUserInfo>,
  );

  return workPeriod.value.timeOffs.reduce(
    (acc: Record<string, PlanningUserInfo>, timeOff: TimeOff) => {
      const { user, ...timeOffData } = timeOff;
      const currentUserData = acc[user.id];

      if (!currentUserData) {
        return acc;
      }

      return {
        ...acc,
        [user.id]: {
          ...currentUserData,
          timeOffs: [...currentUserData.timeOffs, timeOffData],
        },
      };
    },
    usersWithPlannedTime,
  );
});

const usersArray = computed<PlanningUserInfo[]>(() =>
  Object.values(planningUsers.value),
);

const nbDays = computed(() => endDate.value.diff(startDate.value, "days"));
const workingDays = computed(
  () =>
    Array.from(Array(nbDays.value).keys()).reduce(
      (acc, offset) => {
        const newDate = dayjs(startDate.value).add(offset, "days");
        if (newDate.day() > 0 && newDate.day() < 6) {
          const month = newDate.month();
          const { lastMonth, list } = acc;
          return {
            lastMonth: month,
            list: [
              ...list,
              {
                month,
                date: newDate,
                weekDay: newDate.day(),
                dayNb: newDate.date(),
                firstDayOfMonth: lastMonth !== month,
                firstDayOfWeek: newDate.day() === 1,
              },
            ],
          };
        }
        return acc;
      },
      {
        lastMonth: -1,
        list: [] as {
          month: number;
          date: dayjs.Dayjs;
          weekDay: number;
          dayNb: number;
          firstDayOfMonth: boolean;
          firstDayOfWeek: boolean;
        }[],
      },
    ).list,
);

const months = computed(() =>
  Object.values(
    workingDays.value.reduce(
      (acc, day) => {
        const month = acc[day.month];
        if (month) {
          return {
            ...acc,
            [day.month]: {
              ...month,
              nbDays: month.nbDays + 1,
            },
          };
        }
        return {
          ...acc,
          [day.month]: {
            id: day.month,
            nbDays: 1,
          },
        };
      },
      {} as Record<number, { id: number; nbDays: number }>,
    ),
  ),
);

const nbWorkingDays = computed(() => workingDays.value.length);
const projectColor = computed(() => workPeriodResult.value?.project?.color);
const nbUsers = computed(() => usersArray.value.length);

// Project data for sprint planning (with velocity for capacity calculations)
const projectForPlanning = computed(() => {
  const project = workPeriodResult.value?.project;
  if (!project) return null;
  return {
    id: project.id,
    estimationUnit: project.estimationUnit,
    velocity: project.velocity,
  };
});

const isHoliday = (date: Date | dayjs.Dayjs) => {
  return workPeriod.value?.holidays?.some((h: { date: string }) =>
    dayjs(h.date).isSame(date, "day"),
  );
};

const isTimeOff = (
  date: Date | dayjs.Dayjs,
  timeOffs: TimeOffWithoutUser[] = [],
) => {
  return timeOffs.some((to) =>
    dayjs(date).isBetween(to.startDate, to.endDate, "day", "[]"),
  );
};

const isPlanned = (
  date: Date | dayjs.Dayjs,
  plannedTimes: PlannedTimeWithoutUser[] = [],
) => {
  return plannedTimes.some((pt) => {
    if (dayjs(date).isBetween(pt.startDate, pt.endDate, "day", "[]"))
      return true;
    if (pt.frequency === PlannedTimeFrequency.MONTHLY) {
      return (
        dayjs(date).date() >= dayjs(pt.startDate).date() &&
        dayjs(date).date() <= dayjs(pt.endDate).date()
      );
    } else if (pt.frequency === PlannedTimeFrequency.WEEKLY) {
      return (
        dayjs(date).day() >= dayjs(pt.startDate).day() &&
        dayjs(date).day() <= dayjs(pt.endDate).day()
      );
    } else if (pt.frequency === PlannedTimeFrequency.YEARLY) {
      return (
        dayjs(date).date() >= dayjs(pt.startDate).date() &&
        dayjs(date).date() <= dayjs(pt.endDate).date() &&
        dayjs(date).month() >= dayjs(pt.startDate).month() &&
        dayjs(date).month() <= dayjs(pt.endDate).month()
      );
    } else if (pt.frequency === PlannedTimeFrequency.EVERY_2_WEEKS) {
      if (dayjs(pt.startDate).diff(date, "weeks") % 2 !== 0) return false;
      return (
        dayjs(date).day() >= dayjs(pt.startDate).day() &&
        dayjs(date).day() <= dayjs(pt.endDate).day()
      );
    } else if (pt.frequency === PlannedTimeFrequency.EVERY_3_WEEKS) {
      if (dayjs(pt.startDate).diff(date, "weeks") % 3 !== 0) return false;
      return (
        dayjs(date).day() >= dayjs(pt.startDate).day() &&
        dayjs(date).day() <= dayjs(pt.endDate).day()
      );
    } else if (pt.frequency === PlannedTimeFrequency.EVERY_6_WEEKS) {
      if (dayjs(pt.startDate).diff(date, "weeks") % 6 !== 0) return false;
      return (
        dayjs(date).day() >= dayjs(pt.startDate).day() &&
        dayjs(date).day() <= dayjs(pt.endDate).day()
      );
    } else if (pt.frequency === PlannedTimeFrequency.EVERY_3_MONTHS) {
      if (dayjs(pt.startDate).diff(date, "months") % 3 !== 0) return false;
      return (
        dayjs(date).date() >= dayjs(pt.startDate).date() &&
        dayjs(date).date() <= dayjs(pt.endDate).date()
      );
    } else if (pt.frequency === PlannedTimeFrequency.EVERY_6_MONTHS) {
      if (dayjs(pt.startDate).diff(date, "months") % 6 !== 0) return false;
      return (
        dayjs(date).date() >= dayjs(pt.startDate).date() &&
        dayjs(date).date() <= dayjs(pt.endDate).date()
      );
    }
  });
};

const isWorkingDay = (dayNb: number, workDayHours: number[] | undefined) => {
  if (!workDayHours) return true;
  return workDayHours[dayNb] > 0;
};

const isToday = (date: dayjs.Dayjs) => date.isSame(dayjs(), "day");

const visibleSprintItems = computed<SprintLaneItem[]>(() => {
  if (!workingDays.value.length) return [];

  const firstVisibleDay = workingDays.value[0].date.startOf("day");
  const lastVisibleDay =
    workingDays.value[workingDays.value.length - 1].date.endOf("day");

  return sprints.value
    .filter((sprint) => sprint.active)
    .filter((sprint) => {
      const sprintStart = dayjs(sprint.startDate);
      const sprintEnd = dayjs(sprint.endDate);
      return (
        (sprintStart.isBefore(lastVisibleDay) &&
          sprintEnd.isAfter(firstVisibleDay)) ||
        sprintStart.isSame(firstVisibleDay, "day") ||
        sprintEnd.isSame(lastVisibleDay, "day")
      );
    })
    .map((sprint) => {
      let startIndex = workingDays.value.findIndex(
        (day) =>
          day.date.isSame(dayjs(sprint.startDate), "day") ||
          day.date.isAfter(dayjs(sprint.startDate), "day"),
      );

      let endIndex = -1;
      for (let index = workingDays.value.length - 1; index >= 0; index -= 1) {
        const day = workingDays.value[index];
        if (
          day.date.isSame(dayjs(sprint.endDate), "day") ||
          day.date.isBefore(dayjs(sprint.endDate), "day")
        ) {
          endIndex = index;
          break;
        }
      }

      if (startIndex === -1) startIndex = 0;
      if (endIndex === -1) endIndex = workingDays.value.length - 1;

      return {
        ...sprint,
        startIndex,
        endIndex,
      };
    })
    .filter((sprint) => sprint.startIndex <= sprint.endIndex)
    .sort((a, b) => {
      if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
      return a.endIndex - b.endIndex;
    });
});

const sprintLanes = computed<SprintLaneItem[][]>(() => {
  const lanes: SprintLaneItem[][] = [];

  visibleSprintItems.value.forEach((sprint) => {
    const availableLane = lanes.find((lane) => {
      const lastSprint = lane[lane.length - 1];
      return !lastSprint || lastSprint.endIndex < sprint.startIndex;
    });

    if (availableLane) {
      availableLane.push(sprint);
    } else {
      lanes.push([sprint]);
    }
  });

  return lanes;
});

const sprintLaneCount = computed(() => sprintLanes.value.length);

const getSprintGridStyle = (sprint: SprintLaneItem, laneIndex: number) => ({
  gridColumn: `${sprint.startIndex + 2} / span ${
    sprint.endIndex - sprint.startIndex + 1
  }`,
  gridRow: `${laneIndex + 3}`,
  "--sprint-color": sprint.color || "var(--ao-color-primary-base)",
});

const getUserGridRow = (userIndex: number) =>
  sprintLaneCount.value + userIndex + 3;

const gridContentRows = computed(
  () => 1 + sprintLaneCount.value + nbUsers.value,
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        class: "control-btn toggle-btn",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (isPlanningExpanded.value = !isPlanningExpanded.value))
      }, [
        _createVNode(_unref(Icon), {
          icon: 
            isPlanningExpanded.value
              ? 'material-symbols:expand-more'
              : 'material-symbols:chevron-right'
          ,
          class: "toggle-icon"
        }, null, 8, ["icon"]),
        _createTextVNode(" " + _toDisplayString(isPlanningExpanded.value
            ? _ctx.$t("planning.hidePlanning")
            : _ctx.$t("planning.showPlanning")), 1)
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          class: "control-btn",
          onClick: goToToday
        }, "Today"),
        _createElementVNode("button", {
          class: "control-btn icon-btn",
          onClick: previousPeriod
        }, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("span", { class: "arrow" }, "<", -1)
        ]))]),
        _createElementVNode("span", _hoisted_4, _toDisplayString(monthRangeLabel.value), 1),
        _createElementVNode("button", {
          class: "control-btn icon-btn",
          onClick: nextPeriod
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("span", { class: "arrow" }, ">", -1)
        ]))])
      ], 512), [
        [_vShow, isPlanningExpanded.value]
      ])
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _cache[4] || (_cache[4] = _createElementVNode("div", {
          class: "column--name",
          style: {"grid-row":"1"}
        }, null, -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(months.value, (month, monthIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: month.id,
            style: _normalizeStyle({
            gridColumn: `${
              2 +
              months.value
                .slice(0, monthIndex)
                .reduce((total, currentMonth) => total + currentMonth.nbDays, 0)
            } / span ${month.nbDays}`,
            gridRow: 1,
          }),
            class: _normalizeClass([
            'header',
            'month',
            {
              'month--separated': monthIndex > 0,
            },
          ])
          }, _toDisplayString(_unref(dayjs)().month(month.id).format("MMMM YYYY")), 7))
        }), 128)),
        _cache[5] || (_cache[5] = _createElementVNode("div", {
          class: "column--name",
          style: {"grid-row":"2"}
        }, null, -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workingDays.value, (day, dayIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: day.date.toString(),
            class: _normalizeClass([
            'header',
            'day-header',
            {
              'day-header--today': isToday(day.date),
              'first-day-of-month': day.firstDayOfMonth,
              'first-day-of-week': !day.firstDayOfMonth && day.firstDayOfWeek,
            },
          ]),
            style: _normalizeStyle({
            gridColumn: dayIndex + 2,
            gridRow: 2,
          })
          }, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(day.dayNb), 1),
            _createElementVNode("span", _hoisted_8, _toDisplayString(day.date.format("ddd")), 1)
          ], 6))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workingDays.value, (day, dayIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `background-${day.date.toString()}`,
            class: _normalizeClass(["planning-column-background", { 'planning-column-background--today': isToday(day.date) }]),
            style: _normalizeStyle({
            gridColumn: dayIndex + 2,
            gridRow: `2 / span ${gridContentRows.value}`,
          })
          }, null, 6))
        }), 128)),
        (sprintLaneCount.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "column--name sprint-lane-name",
              style: _normalizeStyle({
            gridRow: `3 / span ${sprintLaneCount.value}`,
          })
            }, " Sprints ", 4))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sprintLanes.value, (lane, laneIndex) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: `sprint-lane-${laneIndex}`
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lane, (sprint) => {
              return (_openBlock(), _createElementBlock("div", {
                key: sprint.id,
                class: "sprint-bar",
                style: _normalizeStyle(getSprintGridStyle(sprint, laneIndex)),
                title: `${getSprintName(sprint)} · ${_unref(dayjs)(
              sprint.startDate,
            ).format('DD/MM/YY')} - ${_unref(dayjs)(sprint.endDate).format(
              'DD/MM/YY',
            )}`
              }, [
                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "sprint-bar__dot" }, null, -1)),
                _createElementVNode("span", _hoisted_10, _toDisplayString(getSprintName(sprint)), 1)
              ], 12, _hoisted_9))
            }), 128))
          ], 64))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(usersArray.value, (userInfo, userIndex) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: userInfo.user.id
          }, [
            _createElementVNode("div", {
              class: "column--name user-name",
              style: _normalizeStyle({ gridRow: getUserGridRow(userIndex) })
            }, _toDisplayString(userInfo.user.firstName), 5),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workingDays.value, (day, dayIndex) => {
              return (_openBlock(), _createElementBlock("div", {
                key: `${userInfo.user.id}_${day.date.toString()}`,
                class: _normalizeClass([
              'day',
              {
                'day--time-off':
                  isHoliday(day.date) ||
                  isTimeOff(day.date, userInfo.timeOffs) ||
                  !isWorkingDay(day.weekDay, userInfo.user.workDayHours),
                'day--planned':
                  !isHoliday(day.date) &&
                  !isTimeOff(day.date, userInfo.timeOffs) &&
                  isWorkingDay(day.weekDay, userInfo.user.workDayHours) &&
                  isPlanned(day.date, userInfo.plannedTimes),
                'first-day-of-month': day.firstDayOfMonth,
                'first-day-of-week': !day.firstDayOfMonth && day.firstDayOfWeek,
              },
            ]),
                style: _normalizeStyle({
              gridColumn: dayIndex + 2,
              gridRow: getUserGridRow(userIndex),
            })
              }, null, 6))
            }), 128))
          ], 64))
        }), 128))
      ])
    ], 512), [
      [_vShow, isPlanningExpanded.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_11, [
      _createElementVNode("ul", _hoisted_12, [
        _createVNode(GraphLegendItem, {
          label: _ctx.$t('planning.legend.dayWorked'),
          color: projectColor.value
        }, null, 8, ["label", "color"]),
        _createVNode(GraphLegendItem, {
          label: _ctx.$t('planning.legend.dayTimeOff'),
          color: "var(--color-neutral-light)"
        }, null, 8, ["label"])
      ])
    ], 512), [
      [_vShow, isPlanningExpanded.value]
    ]),
    _createVNode(_unref(AoSprintPlanning), {
      sprints: sprints.value,
      activities: activities.value,
      "selected-activity": selectedActivity.value,
      "is-editable": props.isEditable,
      users: projectUsers.value,
      "user-id": currentUserId.value,
      statuses: activityStatuses.value,
      "dev-statuses": activityDevStatuses.value,
      "is-task-checkable": () => true,
      "attachment-enabled": true,
      "load-attachment-preview": loadAttachmentPreview,
      "planning-data": sprintPlanningData.value,
      projects: projectForPlanning.value ? [projectForPlanning.value] : [],
      "business-day-hours": 8,
      onSelectActivity: selectActivity,
      onUpdateSprint: handleUpdateSprint,
      onArchiveSprint: handleArchiveSprint,
      onUnarchiveSprint: handleUnarchiveSprint,
      onCreateSprint: handleCreateSprint,
      onActivityChange: handleActivityChange,
      onUpdateActivity: handleUpdateActivity,
      onAddSubtask: handleAddSubtask,
      onUpdateSubtask: handleUpdateSubtask,
      onDeleteSubtask: handleDeleteSubtask,
      onAddFiles: handleAddFiles,
      onRemoveFile: handleRemoveFile,
      onAddComment: handleAddComment,
      onUpdateComment: handleUpdateComment,
      onRemoveComment: handleRemoveComment
    }, null, 8, ["sprints", "activities", "selected-activity", "is-editable", "users", "user-id", "statuses", "dev-statuses", "planning-data", "projects"])
  ]))
}
}

})